import { supabase } from "@/lib/supabaseClient";
import { PayrollPosition } from "@/types/payroll-admin";

export type ServiceStyle = "efficient" | "balanced" | "luxury";

export interface PositionState {
  positionId: string;
  positionName: string;
  departmentId: string;
  included: boolean;
  calculatedFTEs: number;
  monthlySalary: number;
  overrideFTEs: number | null;
  overrideSalary: number | null;
}

export interface PayrollTotals {
  totalFTEs: string;
  monthlyPayroll: number;
  socialCharges: number;
  totalMonthly: number;
  annualCost: number;
}

export interface PayrollFullData {
  rooms: number;
  totals: PayrollTotals;
  savedAt: string;
  marketId: string;
  positions: PositionState[];
  serviceStyle: ServiceStyle;
  socialChargesPercent: number;
}

export interface PayrollDataRecord {
  id: string;
  property_id: string;
  data: PayrollFullData;
  market_id: string;
  service_style: string;
  rooms: number;
  total_annual: number;
  total_monthly: number;
  total_ftes: number;
  created_at: string;
  updated_at: string;
}

export interface PayrollData {
  rooms: number;
  totals: {
    totalFTEs: string;
    annualCost: number;
    totalMonthly: number;
    socialCharges: number;
    monthlyPayroll: number;
  };
  savedAt: string;
  marketId: string;
  positions: PayrollPosition[];
  serviceStyle: string;
  socialChargesPercent: number;
}

export interface PayrollSummary {
  dataId: string;
  totalAnnual: number;
  totalMonthly: number;
  totalFTEs: string;
  lastUpdated: string;
}

export interface Payroll {
  id: string;
  property_id: string;
  data: {
    rooms: number;
    totals: {
      totalFTEs: string;
      annualCost: number;
      totalMonthly: number;
      socialCharges: number;
      monthlyPayroll: number;
    };
    savedAt: string;
    marketId: string;
    positions: Array<{
      included: boolean;
      positionId: string;
      departmentId: string;
      positionName: string;
      monthlySalary: number;
      calculatedFTEs: number;
    }>;
    serviceStyle: string;
    socialChargesPercent: number;
  };
  total_ftes: number;
  total_annual: number;
  total_monthly: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Recompute totals from a list of positions.
 */
function recalculateTotals(
  positions: PositionState[],
  socialChargesPercent: number,
): PayrollTotals {
  const included = positions.filter((p) => p.included);

  const totalFTEsNum = included.reduce(
    (sum, p) => sum + (p.overrideFTEs ?? p.calculatedFTEs),
    0,
  );
  const monthlyPayroll = included.reduce(
    (sum, p) =>
      sum +
      (p.overrideSalary ?? p.monthlySalary) *
        (p.overrideFTEs ?? p.calculatedFTEs),
    0,
  );
  const socialCharges = monthlyPayroll * (socialChargesPercent / 100);
  const totalMonthly = monthlyPayroll + socialCharges;
  const annualCost = totalMonthly * 12;

  return {
    totalFTEs: totalFTEsNum.toFixed(1),
    monthlyPayroll,
    socialCharges,
    totalMonthly,
    annualCost,
  };
}

/**
 * Fetch full record by property_id (internal helper).
 */
async function fetchRecordByPropertyId(
  propertyId: string,
): Promise<PayrollDataRecord | null> {
  const { data, error } = await supabase
    .from("property_payroll_data")
    .select("*")
    .eq("property_id", propertyId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data;
}

export const deletePayrollById = async (dataId: string): Promise<void> => {
  const { error } = await supabase
    .from("property_payroll_data")
    .delete()
    .eq("id", dataId);

  if (error) throw error;
};

export const savePayrollData = async (
  propertyId: string,
  payrollData: PayrollFullData,
): Promise<PayrollDataRecord> => {
  const { data, error } = await supabase
    .from("property_payroll_data")
    .upsert(
      {
        property_id: propertyId,
        data: payrollData,
        market_id: payrollData.marketId,
        service_style: payrollData.serviceStyle,
        rooms: payrollData.rooms,
        total_annual: payrollData.totals.annualCost,
        total_monthly: payrollData.totals.totalMonthly,
        total_ftes: parseFloat(payrollData.totals.totalFTEs),
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: "property_id",
      },
    )
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updatePayrollJsonOnly = async (
  dataId: string,
  updatedData: Partial<PayrollFullData>,
): Promise<void> => {
  const { error } = await supabase
    .from("property_payroll_data")
    .update({
      data: updatedData,
      updated_at: new Date().toISOString(),
    })
    .eq("id", dataId);

  if (error) throw error;
};

export const getPayrollData = async (
  propertyId: string,
): Promise<PayrollFullData | null> => {
  const { data, error } = await supabase
    .from("property_payroll_data")
    .select("data")
    .eq("property_id", propertyId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }

  return data?.data;
};

export const getPayrollDataById = async (
  dataId: string,
): Promise<PayrollDataRecord | null> => {
  try {
    const { data, error } = await supabase
      .from("property_payroll_data")
      .select("*")
      .eq("id", dataId)
      .single();

    if (error) {
      console.error("Error fetching payroll data by id:", error);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error in getPayrollDataById:", error);
    return null;
  }
};

export const getPayrollSummary = async (
  propertyId: string,
): Promise<PayrollSummary | null> => {
  const { data, error } = await supabase
    .from("property_payroll_data")
    .select("id, total_annual, total_monthly, total_ftes, updated_at")
    .eq("property_id", propertyId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }

  return {
    dataId: data.id,
    totalAnnual: data.total_annual,
    totalMonthly: data.total_monthly,
    totalFTEs: data.total_ftes?.toString() || "0",
    lastUpdated: data.updated_at,
  };
};

export const deletePayrollData = async (propertyId: string): Promise<void> => {
  const { error } = await supabase
    .from("property_payroll_data")
    .delete()
    .eq("property_id", propertyId);

  if (error) throw error;
};

export const getPayrollSummaryForDisplay = async (
  propertyId: string,
): Promise<{
  totalAnnual: number;
  totalMonthly: number;
  lastUpdated: string;
} | null> => {
  const summary = await getPayrollSummary(propertyId);
  if (summary) {
    return {
      totalAnnual: summary.totalAnnual,
      totalMonthly: summary.totalMonthly,
      lastUpdated: summary.lastUpdated,
    };
  }

  const { data } = await supabase
    .from("properties")
    .select("kpis")
    .eq("id", propertyId)
    .single();

  if (data?.kpis?.payroll) {
    const payroll = data.kpis.payroll;
    return {
      totalAnnual: payroll.totalAnnual || payroll.annualCost || 0,
      totalMonthly: payroll.totalMonthly || payroll.monthlyPayroll || 0,
      lastUpdated:
        payroll.lastUpdated || payroll.savedAt || new Date().toISOString(),
    };
  }

  return null;
};

export const syncPositionsToDatabase = async (
  propertyId: string,
  newPositions: PositionState[],
  newTotals: PayrollTotals,
) => {
  const currentData = await getPayrollData(propertyId);

  if (!currentData) throw new Error("No payroll data found to update");

  const updatedFullData: PayrollFullData = {
    ...currentData,
    positions: newPositions,
    totals: newTotals,
    savedAt: new Date().toISOString(),
  };

  return await savePayrollData(propertyId, updatedFullData);
};

// ─── Role-level CRUD ──────────────────────────────────────────────────────────

/**
 * Add a new role (position) to the payroll of a property.
 * Totals are recomputed automatically.
 */
export const addRole = async (
  propertyId: string,
  newPosition: Omit<PositionState, "positionId">,
): Promise<PayrollDataRecord> => {
  const record = await fetchRecordByPropertyId(propertyId);
  if (!record) throw new Error("Payroll record not found for this property");

  const fullData: PayrollFullData = record.data;

  const positionWithId: PositionState = {
    ...newPosition,
    positionId: crypto.randomUUID(),
  };

  const updatedPositions = [...fullData.positions, positionWithId];
  const updatedTotals = recalculateTotals(
    updatedPositions,
    fullData.socialChargesPercent,
  );

  const updatedFullData: PayrollFullData = {
    ...fullData,
    positions: updatedPositions,
    totals: updatedTotals,
    savedAt: new Date().toISOString(),
  };

  return await savePayrollData(propertyId, updatedFullData);
};

/**
 * Update an existing role by positionId.
 * Pass only the fields you want to change; totals are recomputed automatically.
 */
export const updateRole = async (
  propertyId: string,
  positionId: string,
  updates: Partial<Omit<PositionState, "positionId">>,
): Promise<PayrollDataRecord> => {
  const record = await fetchRecordByPropertyId(propertyId);
  if (!record) throw new Error("Payroll record not found for this property");

  const fullData: PayrollFullData = record.data;

  const positionIndex = fullData.positions.findIndex(
    (p) => p.positionId === positionId,
  );
  if (positionIndex === -1) throw new Error(`Position ${positionId} not found`);

  const updatedPositions = fullData.positions.map((p) =>
    p.positionId === positionId ? { ...p, ...updates } : p,
  );

  const updatedTotals = recalculateTotals(
    updatedPositions,
    fullData.socialChargesPercent,
  );

  const updatedFullData: PayrollFullData = {
    ...fullData,
    positions: updatedPositions,
    totals: updatedTotals,
    savedAt: new Date().toISOString(),
  };

  return await savePayrollData(propertyId, updatedFullData);
};

/**
 * Delete a role by positionId.
 * Totals are recomputed automatically.
 */
export const deleteRole = async (
  propertyId: string,
  positionId: string,
): Promise<PayrollDataRecord> => {
  const record = await fetchRecordByPropertyId(propertyId);
  if (!record) throw new Error("Payroll record not found for this property");

  const fullData: PayrollFullData = record.data;

  const updatedPositions = fullData.positions.filter(
    (p) => p.positionId !== positionId,
  );

  const updatedTotals = recalculateTotals(
    updatedPositions,
    fullData.socialChargesPercent,
  );

  const updatedFullData: PayrollFullData = {
    ...fullData,
    positions: updatedPositions,
    totals: updatedTotals,
    savedAt: new Date().toISOString(),
  };

  return await savePayrollData(propertyId, updatedFullData);
};

/**
 * Toggle the `included` flag for a role (include ↔ suggest).
 */
export const toggleRoleIncluded = async (
  propertyId: string,
  positionId: string,
): Promise<PayrollDataRecord> => {
  const record = await fetchRecordByPropertyId(propertyId);
  if (!record) throw new Error("Payroll record not found for this property");

  const fullData: PayrollFullData = record.data;
  const position = fullData.positions.find((p) => p.positionId === positionId);
  if (!position) throw new Error(`Position ${positionId} not found`);

  return updateRole(propertyId, positionId, { included: !position.included });
};

export const updateSocialChargesPercent = async (
  propertyId: string,
  socialChargesPercent: number,
): Promise<void> => {
  const currentData = await getPayrollData(propertyId);
  if (!currentData) throw new Error("No payroll data found");

  const updatedTotals = recalculateTotals(
    currentData.positions,
    socialChargesPercent,
  );

  const updatedFullData: PayrollFullData = {
    ...currentData,
    socialChargesPercent,
    totals: updatedTotals,
    savedAt: new Date().toISOString(),
  };

  await savePayrollData(propertyId, updatedFullData);
};
